import { Injectable } from '@angular/core';
import { AnalyticsEventName } from '../models/tracking.model';
import { HttpService } from './http.service';
import { environment } from '@env/environment';
import { Capacitor } from '@capacitor/core';
import { FirebaseAnalytics } from '@capacitor-community/firebase-analytics';
import { CredentialsService } from '@app/auth';
import { GeneralService } from './general.service';

declare var dataLayer: any;

@Injectable({
  providedIn: 'root',
})
export class TrackingService {
  private hasIdentified = false;

  constructor(private httpS: HttpService, private credentialS: CredentialsService, public generalS: GeneralService) {
    this.initializeIntegrations();
  }

  private initializeIntegrations(): void {
    // ---------- FACEBOOK PIXEL ----------
    if (environment.tracking.facebookPixel.enabled) {
      (window as any).fbq ||= function () {
        (window as any).fbq.callMethod
          ? (window as any).fbq.callMethod.apply((window as any).fbq, arguments)
          : (window as any).fbq.queue.push(arguments);
      };
      if (!(window as any)._fbq) (window as any)._fbq = (window as any).fbq;
      (window as any).fbq.push = (window as any).fbq;
      (window as any).fbq.loaded = true;
      (window as any).fbq.version = '2.0';
      (window as any).fbq.queue = [];
    }

    // ---------- GOOGLE TAG MANAGER ----------
    if (environment.tracking.googleTagManager.enabled) {
      (window as any).dataLayer = (window as any).dataLayer || [];
      (window as any).dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });

      const gtmScript = document.createElement('script');
      gtmScript.async = true;
      gtmScript.src = `https://www.googletagmanager.com/gtm.js?id=${environment.tracking.googleTagManager.id}`;
      document.head.appendChild(gtmScript);
    }

    // ---------- FIREBASE (Capacitor) ----------
    if (environment.tracking.firebase.enabled) {
      FirebaseAnalytics.initializeFirebase(environment.firebaseConfig);
    }
  }

  public async setUserId(): Promise<void> {
    const uid = this.credentialS.credentials?.id;
    if (!uid) return;

    if (Capacitor.getPlatform() === 'web') {
      if ((window as any).gtag) {
        // Usa el ID de medición de GA4 (G-XXXXXXXX)
        (window as any).gtag('config', environment.firebaseConfig.measurementId, {
          user_id: uid,
          id_user: uid, // Propiedad adicional para GA4
        });
        //  console.log('[GA4] User-ID establecido:', uid);
      }

      // Agregar id_user a GTM dataLayer
      if (environment.tracking.googleTagManager.enabled && (window as any).dataLayer) {
        (window as any).dataLayer.push({
          user_id: uid,
          id_user: uid,
        });
      }
    } else {
      try {
        await FirebaseAnalytics.setUserId({ userId: uid });
        // Establecer id_user como propiedad de usuario en Firebase Analytics
        await FirebaseAnalytics.setUserProperty({ name: 'id_user', value: uid });
        //  console.log('[FirebaseAnalytics] User-ID establecido:', uid);
      } catch (err) {
        console.error('Error al establecer User-ID en Firebase:', err);
      }
    }
  }

  /**
   * 📊 Enviar un evento de tracking a GA4, Pixel, Klaviyo, etc.
   */
  public async trackEvent<K extends AnalyticsEventName>(
    eventName: K,
    params: Record<string, any>,
    isKlaviyo?: boolean,
    isPixel?: boolean
  ): Promise<void> {
    const uid = this.credentialS.credentials?.id;
    const email = this.credentialS.credentials?.email;
    const timestamp = Date.now();

    params['user_id'] = uid;
    params['id_user'] = uid; // Propiedad adicional para GA y GTM
    params['timestamp'] = timestamp;
    params['email'] = email;
    params['device_platform'] = Capacitor.getPlatform();

    if (Capacitor.getPlatform() === 'web') {
      // -------- GA4 via gtag.js --------
      if (environment.tracking.firebase.enabled && (window as any).gtag) {
        (window as any).gtag('event', eventName, params);
        console.log(`[GA4] Evento: ${eventName}`, params);
      }

      // -------- Google Tag Manager (dataLayer) --------
      if (environment.tracking.googleTagManager.enabled && (window as any).dataLayer) {
        (window as any).dataLayer.push({
          event: eventName,
          ...params,
        });
      }

      // -------- Facebook Pixel --------
      if (environment.tracking.facebookPixel.enabled && isPixel) {
        (window as any).fbq?.('trackCustom', eventName, params);
      }
    } else {
     
      try {
        if (uid) {
          await FirebaseAnalytics.setUserId({ userId: uid });
          // Establecer id_user como propiedad de usuario en Firebase Analytics
          await FirebaseAnalytics.setUserProperty({ name: 'id_user', value: uid });
        }

        await FirebaseAnalytics.logEvent({ name: eventName, params });
        console.log(`[FirebaseAnalytics] Evento: ${eventName}`, params);

        await this.httpS.post(`${environment.api.klaviyo.url}`, {
          eventName,
          params,
          isKlaviyo,
          isPixel,
        });
      } catch (err) {
        console.error('Error enviando evento a Cloud Function:', err);
      }
    }
  }
}
